import React, { useEffect, useState, useMemo } from 'react'
import { Select, Checkbox, Input, message } from 'antd'
import { RightOutlined, SearchOutlined } from '@ant-design/icons'
import { getT3CCallNumbers } from '@services/robot'
import './PhoneNumberSelector.less'

export interface PhoneNumberItem {
  value: string
  label: string
}

interface SkillGroupData {
  skillGroupId: number
  skillGroupName: string
  numbers: Array<{ number: string }>
}

interface PhoneNumberSelectorProps {
  value?: string[]
  onChange?: (value?: string[]) => void
  placeholder?: string
}

const PhoneNumberSelector: React.FC<PhoneNumberSelectorProps> = ({
  value,
  onChange,
  placeholder = '请选择主叫号码'
}) => {
  const selectedValues = value || []
  const [skillGroups, setSkillGroups] = useState<SkillGroupData[]>([])
  const [allNumbers, setAllNumbers] = useState<string[]>([])
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  const [searchText, setSearchText] = useState('')

  // 获取电话号码列表
  useEffect(() => {
    const fetchPhoneList = async () => {
      const [res, err] = await getT3CCallNumbers({ page: 1, pageSize: 999, isAIOutbound: true })
      if (err) {
        message.error(`获取号码列表失败: ${err.message}`)
        return
      }

      // 保存技能组数据
      setSkillGroups(res.skillGroupNumbers || [])

      // 从技能组中提取所有号码并去重（用于全选）
      const allNumbersFromGroups = res.skillGroupNumbers?.flatMap(group =>
        group.numbers.map(item => item.number)
      ) || []
      const uniqueNumbers = [...new Set(allNumbersFromGroups)]
      setAllNumbers(uniqueNumbers)
    }

    fetchPhoneList()
  }, [])

  // 根据搜索文本过滤技能组
  const filteredSkillGroups = useMemo(() => {
    if (!searchText.trim()) return skillGroups

    return skillGroups
      .map(group => {
        const matchedNumbers = group.numbers.filter(item =>
          item.number.includes(searchText.trim()) || group.skillGroupName.includes(searchText.trim())
        )
        return matchedNumbers.length > 0
          ? { ...group, numbers: matchedNumbers }
          : null
      })
      .filter(Boolean) as SkillGroupData[]
  }, [skillGroups, searchText])

  // 切换技能组展开/折叠
  const toggleGroupExpand = (skillGroupId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(skillGroupId)) {
        newSet.delete(skillGroupId)
      } else {
        newSet.add(skillGroupId)
      }
      return newSet
    })
  }

  // 全选所有号码
  const handleSelectAll = (e: any) => {
    const checked = e.target.checked
    const newValues = checked ? allNumbers : undefined
    onChange?.(newValues)
  }

  // 选中/取消选中某个技能组
  const handleSkillGroupCheck = (skillGroupId: number, checked: boolean) => {
    const skillGroup = skillGroups.find(sg => sg.skillGroupId === skillGroupId)
    if (!skillGroup) return

    const groupNumbers = skillGroup.numbers.map(n => n.number)
    let newValues: string[] | undefined

    if (checked) {
      // 添加该技能组的所有号码（去重）
      const combined = [...new Set([...selectedValues, ...groupNumbers])]
      newValues = combined
    } else {
      // 移除该技能组的所有号码
      const filtered = selectedValues.filter(v => !groupNumbers.includes(v))
      newValues = filtered.length > 0 ? filtered : undefined
    }
    onChange?.(newValues)
  }

  // 单个号码选择
  const handleNumberCheck = (phoneValue: string, checked: boolean) => {
    let newValues: string[] | undefined
    if (checked) {
      newValues = [...selectedValues, phoneValue]
    } else {
      const filtered = selectedValues.filter(v => v !== phoneValue)
      newValues = filtered.length > 0 ? filtered : undefined
    }
    onChange?.(newValues)
  }

  // 判断技能组是否全选
  const isSkillGroupAllSelected = (skillGroupId: number) => {
    const skillGroup = skillGroups.find(sg => sg.skillGroupId === skillGroupId)
    if (!skillGroup || skillGroup.numbers.length === 0) return false

    const groupNumbers = skillGroup.numbers.map(n => n.number)
    return groupNumbers.every(number => selectedValues.includes(number))
  }

  // 判断技能组是否半选
  const isSkillGroupIndeterminate = (skillGroupId: number) => {
    const skillGroup = skillGroups.find(sg => sg.skillGroupId === skillGroupId)
    if (!skillGroup || skillGroup.numbers.length === 0) return false

    const groupNumbers = skillGroup.numbers.map(n => n.number)
    const selectedCount = groupNumbers.filter(number => selectedValues.includes(number)).length
    return selectedCount > 0 && selectedCount < groupNumbers.length
  }

  const isAllSelected = allNumbers.length > 0 && selectedValues.length === allNumbers.length
  const isIndeterminate = selectedValues.length > 0 && selectedValues.length < allNumbers.length

  const dropdownRender = () => (
    <div className="phone-number-dropdown">
      {/* 搜索框 */}
      <div className="phone-number-search">
        <Input
          prefix={<SearchOutlined />}
          placeholder="分组名称/电话号码"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* 全选 */}
      <div className="phone-number-header">
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={handleSelectAll}
        >
          全选
        </Checkbox>
        <span className="phone-number-clear" onClick={() => onChange?.(undefined)}>清空</span>
      </div>

      {/* 技能组列表 */}
      <div className="phone-number-list">
        {filteredSkillGroups.map(skillGroup => {
          const isExpanded = expandedGroups.has(skillGroup.skillGroupId)
          const isChecked = isSkillGroupAllSelected(skillGroup.skillGroupId)
          const isIndeterminate = isSkillGroupIndeterminate(skillGroup.skillGroupId)

          return (
            <div key={skillGroup.skillGroupId} className="skill-group-section">
              {/* 技能组标题 */}
              <div className="skill-group-header">
                <RightOutlined
                  className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                  onClick={(e) => toggleGroupExpand(skillGroup.skillGroupId, e)}
                />
                <Checkbox
                  checked={isChecked}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSkillGroupCheck(skillGroup.skillGroupId, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span
                  className="skill-group-name"
                  onClick={(e) => toggleGroupExpand(skillGroup.skillGroupId, e)}
                >
                  {skillGroup.skillGroupName}
                </span>
              </div>

              {/* 展开后的号码列表 */}
              {isExpanded && (
                <div className="phone-number-items">
                  {skillGroup.numbers.map(item => (
                    <div key={item.number} className="phone-number-item">
                      <Checkbox
                        checked={selectedValues.includes(item.number)}
                        onChange={(e) => handleNumberCheck(item.number, e.target.checked)}
                      >
                        {item.number}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  const displayValue = selectedValues.length > 0
    ? `已选择 ${selectedValues.length} 个号码`
    : undefined

  return (
    <Select
      mode="multiple"
      value={selectedValues.length > 0 ? selectedValues : undefined}
      placeholder={placeholder}
      dropdownRender={dropdownRender}
      maxTagCount={0}
      maxTagPlaceholder={displayValue}
      style={{ width: '100%' }}
      showArrow
      allowClear
      onClear={() => {
        onChange?.(undefined)
      }}
    />
  )
}

export default PhoneNumberSelector
